import { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Palette,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function systemPromptPreview(text) {
  const t = (text || "").trim();
  if (!t) return "Non défini";
  const oneLine = t.replace(/\s+/g, " ");
  return oneLine.length > 72 ? `${oneLine.slice(0, 72)}…` : oneLine;
}
import { generateSlug } from "../../utils/format";
import { readableTextColor } from "../../utils/branding";
import { generatePassword } from "../../utils/password";
import { MODELS } from "./adminConstants";

const STORAGE_UNITS = [
  { label: "Ko", multiplier: 1024 },
  { label: "Mo", multiplier: 1024 * 1024 },
  { label: "Go", multiplier: 1024 * 1024 * 1024 },
  { label: "To", multiplier: 1024 * 1024 * 1024 * 1024 },
];

export default function ClientFormFields({
  form,
  onChange,
  isNew,
  advancedOpen,
  setAdvancedOpen,
  defaultConfig = null,
}) {
  const [quotaValue, setQuotaValue] = useState("");
  const [quotaUnit, setQuotaUnit] = useState(1024 * 1024);

  useEffect(() => {
    const bytes = form.max_storage_bytes || 524288000;
    let foundUnit = 1024 * 1024;
    let valStr = (bytes / foundUnit).toString();

    for (let i = STORAGE_UNITS.length - 1; i >= 0; i--) {
      if (
        bytes >= STORAGE_UNITS[i].multiplier &&
        bytes % STORAGE_UNITS[i].multiplier === 0
      ) {
        foundUnit = STORAGE_UNITS[i].multiplier;
        valStr = (bytes / foundUnit).toString();
        break;
      }
    }
    /* Sync quota inputs when client record or stored bytes change (derived state). */
    /* eslint-disable react-hooks/set-state-in-effect -- intentional derived UI from form */
    setQuotaValue(valStr);
    setQuotaUnit(foundUnit);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [form.id, isNew, form.max_storage_bytes]);

  const handleQuotaValueChange = (e) => {
    const valStr = e.target.value;
    setQuotaValue(valStr);
    const parsed = parseInt(valStr, 10);
    if (!isNaN(parsed)) {
      onChange("max_storage_bytes", parsed * quotaUnit);
    }
  };

  const handleQuotaUnitChange = (e) => {
    const newMulti = parseInt(e.target.value, 10);
    setQuotaUnit(newMulti);
    const parsed = parseInt(quotaValue, 10);
    if (!isNaN(parsed)) {
      onChange("max_storage_bytes", parsed * newMulti);
    }
  };

  const inputBaseClasses =
    "w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-brand-red/45 focus:border-brand-red transition-all text-stone-900 placeholder:text-stone-400";
  const labelClasses = "block text-sm font-semibold text-stone-700 mb-1.5";
  const fieldClasses = "flex flex-col";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={fieldClasses}>
          <Label htmlFor="field-name" className="mb-1.5 text-stone-700">
            Nom de l'entreprise
          </Label>
          <Input
            id="field-name"
            className="bg-stone-50 focus-visible:ring-brand-red/45"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="Apple Inc."
            required
          />
        </div>
        <div className={fieldClasses}>
          <label htmlFor="field-slug" className={labelClasses}>
            Slug URL{" "}
            <span className="text-stone-400 font-normal ml-1">
              (/c/{form.slug || "…"})
            </span>
          </label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              id="field-slug"
              className={inputBaseClasses}
              value={form.slug}
              onChange={(e) => onChange("slug", e.target.value)}
              placeholder="apple-inc"
              required
            />
            <Button
              type="button"
              variant="secondary"
              className="whitespace-nowrap sm:shrink-0"
              onClick={() => onChange("slug", generateSlug(form.name))}
            >
              Générer
            </Button>
          </div>
        </div>
      </div>

      <div className={fieldClasses}>
        <label htmlFor="field-portal-password" className={labelClasses}>
          Mot de passe du Portail{" "}
          <span className="text-stone-400 font-normal ml-1">
            {isNew ? "(Obligatoire)" : "(Laissez vide si inchangé)"}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            id="field-portal-password"
            className={inputBaseClasses}
            type="text"
            value={form.portal_password || ""}
            onChange={(e) => onChange("portal_password", e.target.value)}
            placeholder="Mot de passe d'accès sécurisé"
            required={isNew}
            minLength={6}
          />
          <Button
            type="button"
            variant="secondary"
            className="whitespace-nowrap"
            onClick={() => onChange("portal_password", generatePassword())}
          >
            Générer
          </Button>
        </div>
        {form.portal_password && isNew && (
          <span className="text-emerald-600 text-sm mt-2 font-medium flex items-center gap-1.5 bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-100">
            ⚠️ Notez ce slug et ce mot de passe et transmettez-les au client
            (ils ne seront plus modifiables/visibles en clair ensuite).
          </span>
        )}
      </div>

      <div className="rounded-xl border border-stone-200 bg-stone-50/80 p-4 space-y-2">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 size-4 shrink-0 rounded border-stone-300 text-brand-red focus:ring-brand-red"
            checked={form.local_agent_enabled === true}
            onChange={(e) => onChange("local_agent_enabled", e.target.checked)}
          />
          <span>
            <span className="block text-sm font-semibold text-stone-700">
              Agent local RAGUIA (synchronisation du dossier)
            </span>
            <span className="mt-1 block text-xs text-stone-500 leading-relaxed">
              Si cette option est désactivée, le portail ne pourra plus émettre
              de jeton agent ni demander de synchronisation ; les appels API de
              l&apos;agent seront refusés.
            </span>
          </span>
        </label>
      </div>

      <Card className="overflow-hidden border-brand-red/10 bg-white">
        <CardHeader className="border-b border-stone-100 bg-stone-50/70 pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900">
            <Palette size={18} className="text-brand-red" /> Aperçu marque
            blanche
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 pt-5 md:grid-cols-[220px_1fr]">
          <div className="flex items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50 p-5">
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt={`Logo ${form.name || "client"}`}
                className="max-h-24 max-w-full object-contain"
              />
            ) : (
              <div className="flex size-20 items-center justify-center rounded-2xl bg-white text-stone-400 shadow-sm ring-1 ring-stone-200">
                <ImageIcon size={28} />
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-bold text-stone-900">
                {form.name || "Nom de l'entreprise"}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                Le logo et la couleur principale sont repris dans le portail
                client et le chatbot. Les actions utilisent une couleur pleine,
                sans dégradé, pour rester lisibles avec toutes les marques.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700">
                <span
                  className="size-4 rounded-full border border-stone-200"
                  style={{ backgroundColor: form.primary_color }}
                />
                Principale {form.primary_color}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700">
                <span
                  className="size-4 rounded-full border border-stone-200"
                  style={{ backgroundColor: form.secondary_color }}
                />
                Secondaire {form.secondary_color}
              </span>
            </div>
            <div
              className="inline-flex rounded-xl px-4 py-2 text-sm font-bold shadow-sm"
              style={{
                backgroundColor: form.primary_color,
                color: readableTextColor(form.primary_color),
              }}
            >
              Exemple bouton chatbot
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={fieldClasses}>
          <label htmlFor="field-primary-color" className={labelClasses}>
            Couleur principale
          </label>
          <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl p-1.5 focus-within:ring-2 focus-within:ring-brand-red/45 focus-within:border-brand-red transition-all">
            <input
              id="field-primary-color"
              type="color"
              className="size-10 rounded-lg cursor-pointer border-0 bg-transparent p-0"
              value={form.primary_color}
              onChange={(e) => onChange("primary_color", e.target.value)}
            />
            <input
              className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-stone-900 px-2 font-mono text-sm"
              value={form.primary_color}
              onChange={(e) => onChange("primary_color", e.target.value)}
              placeholder="#8d282a"
            />
          </div>
        </div>
        <div className={fieldClasses}>
          <label htmlFor="field-secondary-color" className={labelClasses}>
            Couleur secondaire
          </label>
          <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl p-1.5 focus-within:ring-2 focus-within:ring-brand-red/45 focus-within:border-brand-red transition-all">
            <input
              id="field-secondary-color"
              type="color"
              className="size-10 rounded-lg cursor-pointer border-0 bg-transparent p-0"
              value={form.secondary_color}
              onChange={(e) => onChange("secondary_color", e.target.value)}
            />
            <input
              className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-stone-900 px-2 font-mono text-sm"
              value={form.secondary_color}
              onChange={(e) => onChange("secondary_color", e.target.value)}
              placeholder="#010101"
            />
          </div>
        </div>
      </div>

      <div className={fieldClasses}>
        <label htmlFor="field-logo-url" className={labelClasses}>
          Logo URL{" "}
          <span className="text-stone-400 font-normal ml-1">(optionnel)</span>
        </label>
        <input
          id="field-logo-url"
          className={inputBaseClasses}
          value={form.logo_url}
          onChange={(e) => onChange("logo_url", e.target.value)}
          placeholder="https://…/logo.png"
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="field-welcome-message" className={labelClasses}>
          Message de bienvenue
        </label>
        <input
          id="field-welcome-message"
          className={inputBaseClasses}
          value={form.welcome_message}
          onChange={(e) => onChange("welcome_message", e.target.value)}
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="field-storage-quota" className={labelClasses}>
          Quota de stockage maximal
        </label>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            id="field-storage-quota"
            type="text"
            className={inputBaseClasses}
            value={quotaValue}
            onChange={handleQuotaValueChange}
            placeholder="Ex: 500"
            required
            style={{ maxWidth: "150px" }}
          />
          <div className="relative min-w-0 sm:flex-1">
            <select
              className={`${inputBaseClasses} appearance-none pr-10 py-2.5 bg-white cursor-pointer`}
              value={quotaUnit}
              onChange={handleQuotaUnitChange}
            >
              {STORAGE_UNITS.map((u) => (
                <option key={u.label} value={u.multiplier}>
                  {u.label} alloués
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-stone-500">
              <ChevronDown size={16} />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-stone-50/80 p-4 space-y-4">
        <p className="text-sm font-semibold text-stone-700">Quotas chatbot</p>
        <p className="text-xs text-stone-500 leading-relaxed">
          Limitez le nombre de comptes utilisateurs du chatbot et le volume de
          questions (messages utilisateur) par mois calendaire. Laissez vide
          pour aucune limite.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={fieldClasses}>
            <label htmlFor="field-max-users" className={labelClasses}>
              Max utilisateurs chatbot
              <span className="ml-1 font-normal text-stone-400">
                (vide = illimité)
              </span>
            </label>
            <input
              id="field-max-users"
              type="number"
              min={1}
              className={inputBaseClasses}
              value={form.max_chatbot_users ?? ""}
              placeholder="Illimité"
              onChange={(e) => {
                const v = e.target.value.trim();
                if (v === "") {
                  onChange("max_chatbot_users", null);
                  return;
                }
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) onChange("max_chatbot_users", n);
              }}
            />
          </div>
          <div className={fieldClasses}>
            <label htmlFor="field-max-messages" className={labelClasses}>
              Max messages utilisateur / mois
              <span className="ml-1 font-normal text-stone-400">
                (questions, vide = illimité)
              </span>
            </label>
            <input
              id="field-max-messages"
              type="number"
              min={1}
              className={inputBaseClasses}
              value={form.max_chatbot_user_messages_monthly ?? ""}
              placeholder="Illimité"
              onChange={(e) => {
                const v = e.target.value.trim();
                if (v === "") {
                  onChange("max_chatbot_user_messages_monthly", null);
                  return;
                }
                const n = parseInt(v, 10);
                if (!Number.isNaN(n))
                  onChange("max_chatbot_user_messages_monthly", n);
              }}
            />
          </div>
        </div>
      </div>

      <details className="group rounded-xl border border-stone-200 bg-stone-50/80 open:bg-white">
        <summary className="flex cursor-pointer list-none items-start gap-2 px-4 py-3 text-sm [&::-webkit-details-marker]:hidden">
          <ChevronDown
            size={18}
            className="mt-0.5 shrink-0 text-stone-400 transition-transform group-open:rotate-180"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-stone-700">
              Prompt système
              <span className="ml-1 font-normal text-stone-400">
                ({"{client_name}"})
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-xs leading-snug text-stone-500">
              {systemPromptPreview(form.system_prompt)}{" "}
              <span className="whitespace-nowrap text-stone-400">
                — {(form.system_prompt || "").length} car.
              </span>
            </p>
          </div>
        </summary>
        <div className="border-t border-stone-200 px-4 pb-4">
          <textarea
            className={`${inputBaseClasses} mt-3 min-h-[140px] resize-y leading-relaxed sm:min-h-[180px]`}
            value={form.system_prompt}
            onChange={(e) => onChange("system_prompt", e.target.value)}
            rows={5}
          />
        </div>
      </details>

      <button
        type="button"
        className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors py-2"
        onClick={() => setAdvancedOpen((o) => !o)}
      >
        {advancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        Paramètres LLM avancés
      </button>

      {advancedOpen && (
        <div className="bg-stone-50 rounded-xl p-6 border border-stone-200 space-y-6">
          <div className={fieldClasses}>
            <label htmlFor="field-llm-model" className={labelClasses}>
              Modèle Mistral
            </label>
            <div className="relative">
              <select
                id="field-llm-model"
                className={`${inputBaseClasses} appearance-none pr-10`}
                value={form.llm_model}
                onChange={(e) => onChange("llm_model", e.target.value)}
              >
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-stone-500">
                <ChevronDown size={16} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={fieldClasses}>
              <label
                htmlFor="field-temperature"
                className="flex items-center justify-between text-sm font-semibold text-stone-700 mb-3"
              >
                <span>Température</span>
                <span className="bg-white px-2 py-0.5 rounded text-brand-red border border-stone-200 shadow-sm">
                  {form.temperature}
                </span>
              </label>
              <input
                id="field-temperature"
                type="range"
                className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-brand-red"
                min="0"
                max="1"
                step="0.05"
                value={form.temperature}
                onChange={(e) =>
                  onChange("temperature", parseFloat(e.target.value))
                }
              />
              <div className="flex justify-between text-xs text-stone-500 mt-2 font-medium">
                <span>Précis</span>
                <span>Créatif</span>
              </div>
            </div>
            <div className={fieldClasses}>
              <label
                htmlFor="field-max-tokens"
                className="flex items-center justify-between text-sm font-semibold text-stone-700 mb-3"
              >
                <span>Max Tokens</span>
                <span className="bg-white px-2 py-0.5 rounded text-brand-red border border-stone-200 shadow-sm">
                  {form.max_tokens}
                </span>
              </label>
              <input
                id="field-max-tokens"
                type="range"
                className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-brand-red"
                min="256"
                max="4096"
                step="64"
                value={form.max_tokens}
                onChange={(e) =>
                  onChange("max_tokens", parseInt(e.target.value))
                }
              />
              <div className="flex justify-between text-xs text-stone-500 mt-2 font-medium">
                <span>256</span>
                <span>4096</span>
              </div>
            </div>
          </div>

          <div className="border-t border-stone-200 pt-6 mt-2 space-y-4">
            <p className="text-sm font-semibold text-stone-700">
              RAG — récupération documentaire
            </p>
            <p className="text-xs text-stone-500 leading-relaxed">
              <strong>Top K</strong> : nombre de <em>candidats</em> renvoyés par
              la recherche (vecteur / hybride), avant déduplication et avant la
              limite « chunks max » ci-dessous. Ce n’est pas toujours le nombre
              exact de passages envoyés au LLM.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={fieldClasses}>
                <label htmlFor="field-top-k" className={labelClasses}>
                  Top K (recherche)
                  <span className="ml-1 font-normal text-stone-400">
                    (1–100, vide = défaut .env)
                  </span>
                </label>
                <input
                  id="field-top-k"
                  type="number"
                  min={1}
                  max={100}
                  className={inputBaseClasses}
                  value={form.rag_retrieval_top_k ?? ""}
                  placeholder={
                    defaultConfig?.retrieval_top_k != null
                      ? String(defaultConfig.retrieval_top_k)
                      : "ex. 7"
                  }
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (v === "") {
                      onChange("rag_retrieval_top_k", null);
                      return;
                    }
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n)) onChange("rag_retrieval_top_k", n);
                  }}
                />
              </div>
              <div className={fieldClasses}>
                <label htmlFor="field-max-chunks" className={labelClasses}>
                  Chunks max dans le prompt
                  <span className="ml-1 font-normal text-stone-400">
                    (1–30, vide = défaut .env)
                  </span>
                </label>
                <input
                  id="field-max-chunks"
                  type="number"
                  min={1}
                  max={30}
                  className={inputBaseClasses}
                  value={form.rag_max_context_chunks ?? ""}
                  placeholder={
                    defaultConfig?.rag_max_context_chunks != null
                      ? String(defaultConfig.rag_max_context_chunks)
                      : "ex. 5"
                  }
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (v === "") {
                      onChange("rag_max_context_chunks", null);
                      return;
                    }
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n)) onChange("rag_max_context_chunks", n);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
